// Simple translation utilities for Kazakh-Russian

// Basic dictionary for common words and phrases
const TRANSLATION_DICTIONARY = {
  'ru_to_kk': {
    // Common event-related terms
    'пожар': 'өрт',
    'авария': 'апат',
    'ремонт': 'жөндеу',
    'дорога': 'жол',
    'улица': 'көше',
    'дом': 'үй',
    'квартира': 'пәтер',
    'вода': 'су',
    'газ': 'газ',
    'свет': 'жарық',
    'электричество': 'электр',
    'лифт': 'лифт',
    'канализация': 'кәріз',
    'отопление': 'жылыту',
    'мусор': 'қоқыс',
    'транспорт': 'көлік',
    'автобус': 'автобус',
    'проблема': 'мәселе',
    'поломка': 'бұзылу',
    'течь': 'ағу',
    'не работает': 'жұмыс істемейді',
    'сломался': 'бұзылды',
    'требует': 'талап етеді',
    'необходимо': 'керек',
    'срочно': 'шұғыл',
    'опасно': 'қауіпті',
    'помощь': 'көмек',
    'спасибо': 'рақмет',
    'за': 'үшін',
    'полная': 'толық',
    'информация': 'ақпарат',
    'описанию': 'сипаттамаға',
    'нажмите': 'басыңыз',
    'хорошо': 'жақсы',
    'очень': 'өте',
    'работа': 'жұмыс',
    'решено': 'шешілді',
    'зимой': 'қыста',
    'летом': 'жазда',
    'утром': 'таңертең',
    'вечером': 'кешке',
    'ночью': 'түнде',
    'сегодня': 'бүгін',
    'вчера': 'кеше',
    'завтра': 'ертең'
  },
  'kk_to_ru': {
    // Reverse dictionary
    'өрт': 'пожар',
    'апат': 'авария',
    'жөндеу': 'ремонт',
    'жол': 'дорога',
    'көше': 'улица',
    'үй': 'дом',
    'пәтер': 'квартира',
    'су': 'вода',
    'газ': 'газ',
    'жарық': 'свет',
    'электр': 'электричество',
    'лифт': 'лифт',
    'кәріз': 'канализация',
    'жылыту': 'отопление',
    'қоқыс': 'мусор',
    'көлік': 'транспорт',
    'автобус': 'автобус',
    'мәселе': 'проблема',
    'бұзылу': 'поломка',
    'ағу': 'течь',
    'жұмыс істемейді': 'не работает',
    'бұзылды': 'сломался',
    'талап етеді': 'требует',
    'керек': 'необходимо',
    'шұғыл': 'срочно',
    'қауіпті': 'опасно',
    'көмек': 'помощь',
    'рақмет': 'спасибо',
    'үшін': 'за',
    'толық': 'полная',
    'ақпарат': 'информация',
    'сипаттамаға': 'описанию',
    'басыңыз': 'нажмите',
    'жақсы': 'хорошо',
    'өте': 'очень',
    'жұмыс': 'работа',
    'шешілді': 'решено',
    'қыста': 'зимой',
    'жазда': 'летом',
    'таңертең': 'утром',
    'кешке': 'вечером',
    'түнде': 'ночью',
    'бүгін': 'сегодня',
    'кеше': 'вчера',
    'ертең': 'завтра'
  }
};

// Common patterns for translation
const PATTERNS = {
  'ru_to_kk': [
    { from: /улица\s+(.+)/gi, to: '$1 көшесі' },
    { from: /дом\s+(\d+)/gi, to: '$1 үй' },
    { from: /квартира\s+(\d+)/gi, to: '$1 пәтер' },
    { from: /не работает/gi, to: 'жұмыс істемейді' },
    { from: /требует ремонта/gi, to: 'жөндеу керек' },
    { from: /произошла авария/gi, to: 'апат болды' },
    { from: /проблема с/gi, to: 'мәселе бар' }
  ],
  'kk_to_ru': [
    { from: /(.+)\s+көшесі/gi, to: 'улица $1' },
    { from: /(\d+)\s+үй/gi, to: 'дом $1' },
    { from: /(\d+)\s+пәтер/gi, to: 'квартира $1' },
    { from: /жұмыс істемейді/gi, to: 'не работает' },
    { from: /жөндеу керек/gi, to: 'требует ремонта' },
    { from: /апат болды/gi, to: 'произошла авария' },
    { from: /мәселе бар/gi, to: 'проблема с' }
  ]
};

/**
 * Detect the language of the text
 * @param {string} text - Text to analyze
 * @returns {string} - 'kk' for Kazakh, 'ru' for Russian
 */
export function detectLanguage(text) {
  if (!text || typeof text !== 'string') return 'ru';
  
  // Check for Cyrillic characters specific to Kazakh
  const kazakhChars = /[әіңғүұқөһ]/g;
  const kazakhCharMatches = (text.match(kazakhChars) || []).length;
  
  // Check for common Russian words
  const russianWords = /\b(и|в|на|с|по|для|от|при|к|за|через|о|об|что|как|где|когда|почему)\b/gi;
  const russianMatches = (text.match(russianWords) || []).length;
  
  // Check for common Kazakh words (including test words)
  const kazakhWords = /\b(және|мен|бар|жоқ|керек|болды|істеу|жасау|келу|бару|өту|рақмет|көмек|үшін|апат|жойылды|мәселе|шешілді|толық|ақпарат|сипаттамаға|басыңыз)\b/gi;
  const kazakhWordMatches = (text.match(kazakhWords) || []).length;
  
  // Calculate scores - give more weight to Kazakh-specific characters
  const kazakhScore = kazakhCharMatches * 3 + kazakhWordMatches * 2;
  const russianScore = russianMatches;
  
  return kazakhScore > russianScore ? 'kk' : 'ru';
}

/**
 * Simple translation function
 * @param {string} text - Text to translate
 * @param {string} fromLang - Source language ('ru' or 'kk')
 * @param {string} toLang - Target language ('ru' or 'kk')
 * @returns {string} - Translated text
 */
export function translateText(text, fromLang = null, toLang = 'ru') {
  if (!text || typeof text !== 'string') return text;
  
  // Auto-detect language if not provided
  if (!fromLang) {
    fromLang = detectLanguage(text);
  }
  
  // If already in target language, return as is
  if (fromLang === toLang) return text;
  
  const dictKey = `${fromLang}_to_${toLang}`;
  const dictionary = TRANSLATION_DICTIONARY[dictKey];
  const patterns = PATTERNS[dictKey];
  
  if (!dictionary || !patterns) return text;
  
  let translatedText = text;
  
  // Apply pattern-based translations first
  patterns.forEach(pattern => {
    translatedText = translatedText.replace(pattern.from, pattern.to);
  });
  
  // Apply dictionary translations
  Object.keys(dictionary).forEach(key => {
    // Use more flexible regex that works with Cyrillic characters
    const regex = new RegExp(key.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'gi');
    translatedText = translatedText.replace(regex, dictionary[key]);
  });
  
  return translatedText;
}

// Export functions to window for browser console testing
if (typeof window !== 'undefined') {
  window.testTranslation = {
    detectLanguage,
    translateText,
    testKazakhText: () => {
      const texts = [
        "Рақмет көмек үшін",
        "Проблема решена", 
        "Апат жойылды",
        "Мәселе шешілді"
      ];
      
      texts.forEach(text => {
        const detected = detectLanguage(text);
        const toRu = translateText(text, detected, 'ru');
        const toKk = translateText(text, detected, 'kk');
        console.log(`Text: "${text}" | Detected: ${detected} | To RU: "${toRu}" | To KK: "${toKk}"`);
      });
    }
  };
}

/**
 * Get appropriate description based on user's language preference
 * @param {Object} event - Event object with description_ru and description_kk
 * @param {string} userLanguage - User's preferred language
 * @returns {string} - Description in user's language or translated
 */
export function getLocalizedDescription(event, userLanguage = 'ru') {
  if (!event) return '';
  
  const ruDescription = event.description_ru || '';
  const kkDescription = event.description_kk || '';
  
  // If we have the description in user's language, use it
  if (userLanguage === 'ru' && ruDescription) {
    return ruDescription;
  }
  
  if (userLanguage === 'kk' && kkDescription) {
    return kkDescription;
  }
  
  // If we don't have description in user's language, translate from available
  if (userLanguage === 'ru' && kkDescription && !ruDescription) {
    return translateText(kkDescription, 'kk', 'ru');
  }
  
  if (userLanguage === 'kk' && ruDescription && !kkDescription) {
    return translateText(ruDescription, 'ru', 'kk');
  }
  
  // Fallback to available description
  return ruDescription || kkDescription || '';
}

/**
 * Format address for display
 * @param {string} address - Raw address
 * @param {string} language - Display language
 * @returns {string} - Formatted address
 */
export function formatAddress(address, language = 'ru') {
  if (!address) return '';
  
  // Translate address if needed
  const translatedAddress = translateText(address, null, language);
  
  return translatedAddress;
}